/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { InvoiceService } from "@services";
import utils from "@utils";
import classNames from "classnames";
import { Loader, Modal, FolderTree } from "../../../../../components";
import "./TimeCardNotesModal.scss";

const TimeCardNotesModal = props => {
	const { lang } = global;
	const { show, invoiceId, onHide, selected, user } = props;

	const [timeCardNotesLoading, setTimeCardNotesLoading] = useState(false);
	const [timeCardNotesItems, setTimeCardNotesItems] = useState([]);
	const [treeState, setTreeState] = useState(null);

	const filterCheckedItemsWithData = state => {
		const filteredItems = [];
		let combinedData = [];

		function recursiveFilter(node) {
			if (node.checked === 1 && node.data) {
				filteredItems.push(node);
				combinedData = combinedData.concat(node.data);
			}
			if (node.children && node.children.length > 0) {
				node.children.forEach(child => recursiveFilter(child));
			}
		}

		recursiveFilter(state);
		return { filteredItems, combinedData };
	};

	const getCheckedState = (items, key = 'checked') => {
		if (items.every(i => i[key] === 1)) return 1;
		if (items.some(i => i[key] > 0)) return 0.5;
		return 0;
	};

	const buildOfficerItems = (job, jobEventId) => Object.entries(job.officers).map(([officerId, { officer, timecardNotes }]) => {
		const isSelected =
			selected?.JobEventId === Number(jobEventId) &&
			selected?.JobId === Number(job.JobId) &&
			selected?.OfficerId === Number(officerId);

		const shouldBeChecked = isSelected || selected?.JobEventId === undefined;

		return {
			name: `${officer} (${timecardNotes.length})`,
			data: timecardNotes,
			checked: shouldBeChecked ? 1 : 0,
		};
	});

	const buildJobItems = (jobs, jobEventId) => Object.values(jobs).map(job => {
		const officerItems = buildOfficerItems(job, jobEventId);

		return {
			name: job.position,
			checked: getCheckedState(officerItems),
			children: officerItems,
		};
	});

	const buildJobEventsTree = (groupedJobs) => Object.entries(groupedJobs).map(([jobEventId, jobs]) => {
		let notesCount = 0;

		Object.values(jobs).forEach(job => {
			Object.values(job.officers).forEach(({ timecardNotes }) => {
				notesCount += timecardNotes.length;
			});
		});

		const jobItems = buildJobItems(jobs, jobEventId);

		return {
			name: `Job #${jobEventId} (${notesCount})`,
			checked: getCheckedState(jobItems),
			children: jobItems,
		};
	});

	const groupTimeCardNotesByHierarchy = (notes) => {
		const groupedJobs = {};

		notes.forEach(note => {
			const { JobEventId, JobId, JobTitle, UserId, Officer } = note;

			if (!groupedJobs[JobEventId]) {
				groupedJobs[JobEventId] = {};
			}

			if (!groupedJobs[JobEventId][JobId]) {
				groupedJobs[JobEventId][JobId] = {
					JobId,
					position: JobTitle,
					officers: {},
				};
			}

			if (!groupedJobs[JobEventId][JobId].officers[UserId]) {
				groupedJobs[JobEventId][JobId].officers[UserId] = {
					officer: Officer,
					timecardNotes: [],
				};
			}

			groupedJobs[JobEventId][JobId].officers[UserId].timecardNotes.push({ ...note });
		});

		return groupedJobs;
	};

	const getTimeCardNotesForNonRKPayInvoice = async () => {
		try {
			setTimeCardNotesLoading(true);

			const { data } = await InvoiceService.getTimeCardNotesForNonRKPayInvoice(invoiceId);
			const notesWithShowDetails = data.map(note => ({ ...note, showDetails: false }));
			setTimeCardNotesItems(notesWithShowDetails);

			const groupedJobs = groupTimeCardNotesByHierarchy(data);
			const jobEventsChildren = buildJobEventsTree(groupedJobs);

			const newTreeState = {
				name: `Timecard Notes (${data.length})`,
				isOpen: true,
				checked: getCheckedState(jobEventsChildren),
				children: jobEventsChildren,
			};

			setTreeState(newTreeState);

			if (selected !== null) {
				const { combinedData } = filterCheckedItemsWithData(newTreeState);
				setTimeCardNotesItems(combinedData);
			}
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Unable to retrieve timecard notes"),
				timeOut: 1500,
			});
		} finally {
			setTimeCardNotesLoading(false);
		}
	};

	const onTreeStateChange = newTreeState => {
		const { combinedData } = filterCheckedItemsWithData(newTreeState);
		setTimeCardNotesItems(combinedData);
	};

	const onCloseModal = () => {
		setTimeCardNotesItems([]);
		setTreeState(null);
		onHide();
	}

	useEffect(() => {
		if (show && invoiceId) {
			getTimeCardNotesForNonRKPayInvoice();
		}
	}, [show, invoiceId]);

	const toggleDetails = (noteId) => {
		setTimeCardNotesItems(prevItems =>
			prevItems.map(note =>
			note.Id === noteId ? { ...note, showDetails: !note.showDetails } : note
			)
		);
	};

	const formatNoteDate = (note, timeZoneId) => {
		if (note.UpdateDate)
			return utils.formatDate(new Date(`${note.UpdateDate}Z`),"en-US",timeZoneId);

		if (note.DateCreated)
			return utils.formatDate(new Date(`${note.DateCreated}Z`),"en-US",timeZoneId);

		return "N/A";
	};

	return (
		<Modal
			id="timecard-notes-modal"
			show={show}
			onHide={() => onCloseModal()}
			size="xl"
		>
			<Modal.Header closeButton>
				<Modal.Title>{lang.t("Timecard Notes")} </Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Choose>
					<When condition={treeState === null}>
						<div className="row text-center text-muted">
							{lang.t("No Timecard Notes found")}
						</div>
					</When>
					<Otherwise>
						<section className="attachments-section">
							<nav>
								<If condition={treeState !== null}>
									<FolderTree
										data={treeState}
										onChange={onTreeStateChange}
										initCheckedStatus="custom"
										initOpenStatus="custom"
									/>
								</If>
							</nav>
							<main className="attachments-section-content">
								{timeCardNotesItems.length > 0 ? (
									<div className="timecard-notes-table">
										{/* Table Header */}
										<div className="timecard-notes-table-header hidden-xs">
											<div className="row">
												<strong className="col-sm-2">
													{lang.t("Date")}
												</strong>
												<strong className="col-sm-2">
													{lang.t("Job #")}
												</strong>
												<strong className="col-sm-3">
													{lang.t("Position")}
												</strong>
												<strong className="col-sm-3">
													{lang.t("Officer")}
												</strong>
											</div>
										</div>
										{/* Table Body */}
										<div className="timecard-notes-table-body">
											<Choose>
												<When condition={timeCardNotesItems.length > 0}>
													<For
														each="note"
														of={timeCardNotesItems}
														index="index"
													>
														<div
															className="time-card-item"
															key={`${note.Id}`}
														>
															<div
																className={`collapsing-panel row${note.showDetails ? " expanded" : ""}`}
															>
																<div className="col-sm-2">
																	{utils.formatDate(
																		new Date(
																			`${note.JobEventDate}Z`,
																		),
																		"en-US",
																		user.TimeZoneId,
																	)}
																</div>
																<div className="col-sm-2">
																	{note.JobEventId}
																</div>
																<div className="col-sm-3">
																	{note.JobTitle}
																</div>
																<div className="col-sm-3">
																	{note.Officer}
																</div>
																<div className="col-sm-2">
																	<button
																		type="button"
																		className="btn btn-link btn-block text-left"
																		onClick={() =>
																			toggleDetails(
																				note.Id,
																			)
																		}
																	>
																		{lang.t("Details")}
																		<i
																			title="View Details"
																			className={classNames("rkf chevron-icon", {
																				"rkf-chevron-down": !note.showDetails,
																				"rkf-chevron-up": note.showDetails,
																				})}
																		/>
																	</button>
																</div>
																<If condition={note.showDetails}>
																	<div className="col-xs-12 expanded-details ml-4">
																		<div>
																			<strong>{lang.t("Note_")}</strong> {note.Note || "N/A"}
																		</div>
																		<div>
																			<strong>{lang.t("User_")}</strong> {note.UpdatedBy || note.CreatedBy || "N/A"}
																		</div>
																		<div>
																			<strong>{lang.t("Date_")}</strong>{" "}
																			{formatNoteDate(note, user.TimeZoneId)}
																		</div>
																	</div>
																</If>
															</div>
														</div>
													</For>
												</When>
											</Choose>
										</div>
									</div>
								) : (
									<div className="col-xs-12 text-center text-muted display-empty">
										{lang.t("No timecard notes to display_")}
									</div>
								)}
							</main>
						</section>
					</Otherwise>
				</Choose>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn btn-primary"
					id="close-invoice-status-history-modal"
					onClick={() => onCloseModal()}
				>
					{lang.t("Close")}
				</button>
			</Modal.Footer>
			<Loader active={timeCardNotesLoading} />
		</Modal>
	);
};

TimeCardNotesModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	invoiceId: PropTypes.string,
	user: PropTypes.object.isRequired,
	selected: PropTypes.object,
};

TimeCardNotesModal.defaultProps = {
	show: false,
	selected: {},
	onHide: () => {},
	invoiceId: "",
};

export default TimeCardNotesModal;
